// Central catalog of services rendered on the /services listing page
// and on each dynamic /services/:slug detail page.
import {
  Users, Cpu, Briefcase, Award, ClipboardList, Layers,
  GraduationCap, Headphones, HeartHandshake, Wallet, FileSearch,
  UserCheck, Target, BadgeCheck, Globe, Search,
} from "lucide-react";

export const SERVICE_CATALOG = [
  {
    slug: "permanent-hiring",
    icon: Users,
    title: "Permanent Hiring",
    tagline: "Long-term hiring across technical and business functions.",
    hero: "https://images.unsplash.com/photo-1739298061740-5ed03045b280?auto=format&fit=crop&w=1600&q=85",
    image: "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&w=1200&q=80",
    intro: "Permanent hiring is at the heart of every scaling organization. Ascend Tech Solutions runs a structured, requirement-first approach — from job design to onboarding — so every long-term hire is a strong cultural and technical fit.",
    benefits: [
      "Reduce mis-hire risk with a structured evaluation loop",
      "Faster time-to-shortlist with a live sourcing pipeline",
      "Realistic salary benchmarking across cities and bands",
      "Interview coordination and offer negotiation support",
      "Post-offer engagement to reduce dropouts",
    ],
    industries: ["IT & Software", "SaaS & Product", "BFSI", "Manufacturing", "E-commerce", "EdTech"],
  },
  {
    slug: "it-recruitment",
    icon: Cpu,
    title: "IT Recruitment",
    tagline: "Focused technology hiring across engineering, data, cloud and support.",
    hero: "https://images.unsplash.com/photo-1696834137451-f52f471a58bc?auto=format&fit=crop&w=1600&q=85",
    image: "https://images.unsplash.com/photo-1698047681452-08eba22d0c64?auto=format&fit=crop&w=1200&q=80",
    intro: "From backend engineers to platform SREs, data scientists to security specialists — our IT recruiting practice is built around a deep understanding of modern engineering roles, stacks, seniority bands and evolving skill markets across India.",
    benefits: [
      "Access to a curated network of engineering & data talent",
      "Screening for stack depth, not just keyword matches",
      "Support across permanent, contract and C2H models",
      "Guidance on realistic compensation and notice-period expectations",
      "Structured tech-panel scheduling and coordination",
    ],
    industries: ["Software Products", "Cloud & DevOps", "Data & AI", "Cybersecurity", "IT Services", "FinTech"],
  },
  {
    slug: "non-it-recruitment",
    icon: Briefcase,
    title: "Non-IT Recruitment",
    tagline: "Business, operations, commercial and support hiring.",
    hero: "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&w=1600&q=85",
    image: "https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "Non-IT roles carry the operational spine of every business. We recruit across HR, finance, sales, operations, customer success and administration — with the same discipline we apply to technology hiring.",
    benefits: [
      "Recruiters with domain understanding of each function",
      "Pipelines calibrated to role band and city",
      "Coverage across metros and tier-2 cities",
      "Behavioural and situational screening support",
      "Volume-hiring readiness for growth-stage teams",
    ],
    industries: ["BFSI", "BPO & Contact Centres", "Retail & E-commerce", "Healthcare", "Consulting", "Logistics"],
  },
  {
    slug: "executive-search",
    icon: Award,
    title: "Executive Search",
    tagline: "Confidential leadership hiring across CXO, VP and Director bands.",
    hero: "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "Leadership hiring is high-stakes, private and reference-driven. Our executive search practice is retained, confidential and structured — from mandate scoping to onboarding alignment for CXO, VP and senior functional heads.",
    benefits: [
      "Discreet, mapped outreach — no public postings",
      "Reference-verified profiles with quality shortlists",
      "Structured competency-based evaluation",
      "Compensation and equity structuring guidance",
      "Onboarding & first-90-days advisory",
    ],
    industries: ["SaaS & Product", "BFSI", "Consulting", "Manufacturing", "Healthcare", "Consumer"],
  },
  {
    slug: "contract-staffing",
    icon: ClipboardList,
    title: "Contract Staffing",
    tagline: "Flexible workforce solutions including contract and contract-to-hire.",
    hero: "https://images.pexels.com/photos/7580640/pexels-photo-7580640.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "Contract staffing gives you speed and flexibility without long-term overhead. We deploy contract, contract-to-hire and project-based professionals across IT and Non-IT functions with compliant onboarding and ongoing engagement.",
    benefits: [
      "Rapid ramp-up for project or seasonal demand",
      "Statutory-compliant on-roll deployment",
      "Contract-to-hire pathway for permanent conversion",
      "Single point of workforce accountability",
      "Transparent commercials — no hidden markup",
    ],
    industries: ["IT Services", "BFSI", "E-commerce", "Retail", "Logistics", "Manufacturing"],
  },
  {
    slug: "bulk-hiring",
    icon: Layers,
    title: "Bulk Hiring",
    tagline: "Structured coordination for multi-vacancy or volume hiring drives.",
    hero: "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/7580640/pexels-photo-7580640.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "For high-volume drives — customer support, sales, operations, engineering ramp-ups — we run a controlled pipeline with clear SLAs, weekly cadence and end-to-end coordination across sourcing, screening, scheduling and closure.",
    benefits: [
      "Weekly reporting on funnel health",
      "Multi-city coordination through one point of contact",
      "Fast turnaround on shortlists and offer roll-out",
      "Assessment and BGV integration",
      "Post-joining follow-through",
    ],
    industries: ["BPO", "E-commerce", "Retail", "Banking Operations", "Logistics", "Field Sales"],
  },
  {
    slug: "campus-hiring",
    icon: GraduationCap,
    title: "Campus Hiring",
    tagline: "End-to-end campus drives across engineering, management and skill programs.",
    hero: "https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.unsplash.com/photo-1758518729685-f88df7890776?auto=format&fit=crop&w=1200&q=80",
    intro: "Campus programs are one of the strongest talent pipelines when run well. We manage the full campus recruitment cycle — institution partnerships, PPTs, assessments, interviews and offer roll-outs — while building an early employer brand for you.",
    benefits: [
      "Institution shortlisting aligned to your talent needs",
      "Pre-placement talks & employer branding support",
      "Assessment design and coordination",
      "Interview scheduling and results tracking",
      "Post-offer engagement to reduce drop-outs",
    ],
    industries: ["IT Services", "Consulting", "SaaS", "BFSI", "Manufacturing", "Analytics"],
  },
  {
    slug: "bpo-hiring",
    icon: Headphones,
    title: "BPO & Voice Hiring",
    tagline: "Volume hiring for customer support, back-office and voice/non-voice roles.",
    hero: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/7580640/pexels-photo-7580640.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "BPO hiring demands scale, speed and language calibration. We source, screen and coordinate voice, chat and back-office hires across languages and cities with tight SLAs and integrated verification.",
    benefits: [
      "Language and accent-calibrated screening",
      "Rapid pipeline turnaround (days, not weeks)",
      "Coordination across shifts and cities",
      "Post-offer engagement to counter drop-outs",
      "Compliant onboarding & documentation",
    ],
    industries: ["Contact Centres", "Banking Ops", "Telecom", "E-commerce Support", "Healthcare Support", "Travel & Hospitality"],
  },
  {
    slug: "hr-consulting",
    icon: HeartHandshake,
    title: "HR Consulting",
    tagline: "Policy, HR process design and organisation-development advisory.",
    hero: "https://images.unsplash.com/photo-1758518731296-20e24e58846f?auto=format&fit=crop&w=1600&q=85",
    image: "https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?auto=format&fit=crop&w=1200&q=80",
    intro: "For growing organisations, HR is often the difference between chaos and compounding. Our HR consulting practice supports policy design, performance frameworks, HR audits and org-design advisory tailored to your stage and industry.",
    benefits: [
      "HR audit & compliance readiness",
      "Policy and handbook design",
      "Performance & compensation frameworks",
      "Org design & role clarity",
      "Culture & engagement advisory",
    ],
    industries: ["Startups", "SME", "Mid-market", "IT Services", "Manufacturing", "Consumer"],
  },
  {
    slug: "payroll-services",
    icon: Wallet,
    title: "Payroll Services",
    tagline: "Statutory-compliant payroll processing and workforce administration.",
    hero: "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&w=1200&q=80",
    intro: "Reliable payroll is invisible when done right and painful when done wrong. We manage monthly payroll processing, statutory compliance, reimbursements and workforce administration for lean HR teams that need accuracy without overhead.",
    benefits: [
      "Accurate monthly payroll processing",
      "PF, ESIC, PT, TDS and gratuity compliance",
      "Employee self-service payslips",
      "Full & final settlement handling",
      "Audit-ready records and reporting",
    ],
    industries: ["Startups", "SME", "IT Services", "Retail", "Manufacturing", "Consulting"],
  },
  {
    slug: "talent-sourcing",
    icon: FileSearch,
    title: "Talent Sourcing & Screening",
    tagline: "Focused identification and preliminary screening of candidates.",
    hero: "https://images.pexels.com/photos/5439137/pexels-photo-5439137.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/5439137/pexels-photo-5439137.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "Sometimes you just need great sourcing — a shortlist of relevant, screened profiles that your internal team can run interviews on. We deliver focused sourcing as a stand-alone engagement or as part of a broader mandate.",
    benefits: [
      "Role-brief calibration and target-profile map",
      "Focused sourcing across premium channels",
      "Preliminary screening and shortlisting",
      "Weekly progress and market insight",
      "Handover-ready candidate dossiers",
    ],
    industries: ["Any function", "Any city", "Any band", "Any industry"],
  },
  {
    slug: "candidate-assessment",
    icon: UserCheck,
    title: "Candidate Assessment",
    tagline: "Skill, aptitude and behavioural evaluation to reduce mis-hires.",
    hero: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "A structured assessment layer meaningfully reduces mis-hires. We plug into your evaluation stack with technical, aptitude and behavioural assessments delivered inside a coordinated candidate experience.",
    benefits: [
      "Role-mapped assessment design",
      "Coding, aptitude and functional tests",
      "Behavioural & situational interviews",
      "Assessment reports for hiring managers",
      "Bias-aware evaluation protocols",
    ],
    industries: ["Technology", "BFSI", "Consulting", "Manufacturing", "Sales", "Ops"],
  },
  {
    slug: "resume-screening",
    icon: Target,
    title: "Resume Screening",
    tagline: "Fast, role-aligned profile review with structured shortlisting.",
    hero: "https://images.pexels.com/photos/5439137/pexels-photo-5439137.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/5439137/pexels-photo-5439137.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "When you're receiving 500 CVs and only 20 look promising, quality screening is a competitive advantage. We handle high-volume CV screening against your role brief with quick, structured shortlists.",
    benefits: [
      "Structured shortlist against role brief",
      "Skill, experience and location filters",
      "Notice-period and comp-fit validation",
      "Rapid turnaround at scale",
      "Weekly pipeline snapshots",
    ],
    industries: ["Any"],
  },
  {
    slug: "background-verification",
    icon: BadgeCheck,
    title: "Background Verification",
    tagline: "Coordination for employment, education and identity checks.",
    hero: "https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1200",
    intro: "Trust starts with verification. We coordinate structured background verification — employment history, education, identity, address and reference checks — before candidates step into your organisation.",
    benefits: [
      "Employment & education verification",
      "Identity & address checks",
      "Reference and character verification",
      "Court-record and CIBIL checks on request",
      "Turnaround aligned to onboarding timelines",
    ],
    industries: ["BFSI", "Healthcare", "Enterprise IT", "Consulting", "Government contracting", "Any regulated sector"],
  },
  {
    slug: "employer-branding",
    icon: Globe,
    title: "Employer Branding",
    tagline: "Positioning your workplace story to attract better candidates.",
    hero: "https://images.pexels.com/photos/7644081/pexels-photo-7644081.jpeg?auto=compress&cs=tinysrgb&w=1600",
    image: "https://images.unsplash.com/photo-1758518731468-98e90ffd7430?auto=format&fit=crop&w=1200&q=80",
    intro: "In a candidate-first market, employer brand is a magnet. We help articulate your workplace story — EVP, culture assets, careers content — so the right candidates recognise and choose you.",
    benefits: [
      "EVP articulation & careers narrative",
      "Careers-page content & imagery guidance",
      "Employee spotlight and testimonial capture",
      "Social & LinkedIn presence strategy",
      "Recruiter enablement kits",
    ],
    industries: ["Any"],
  },
  {
    slug: "talent-acquisition-advisory",
    icon: Search,
    title: "Talent Acquisition Advisory",
    tagline: "Hiring strategy, funnel design and recruiter enablement.",
    hero: "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&w=1600&q=85",
    image: "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&w=1200&q=80",
    intro: "Whether you're setting up an in-house TA function or optimising an existing one, our advisory practice supports funnel design, recruiter enablement, tooling and metrics that make hiring predictable.",
    benefits: [
      "Funnel design & conversion diagnostics",
      "Recruiter enablement & playbooks",
      "ATS & tooling advisory",
      "Hiring-manager training",
      "TA metrics & reporting frameworks",
    ],
    industries: ["Scaleups", "Mid-market", "Enterprise TA teams"],
  },
];

export function getServiceBySlug(slug) {
  return SERVICE_CATALOG.find((s) => s.slug === slug);
}
